import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    OnChanges,
    SimpleChanges,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { TableComponent } from '../../../core/table/table.component';
import { BaseSearchOptions } from '../../../models/core/table/BaseSearchOptions';
import { FilterDataType } from '../../../models/core/table/FilterDataType';
import { TableColumn } from '../../../models/core/table/TableColumn';
import { TableEvent } from '../../../models/core/table/TableEvent';
import { CustomizerSettingsService } from '../../../customizer-settings/customizer-settings.service';
import {
    CustomerDashboardCounter,
    RecentOrderItem,
} from '../../../models/domain/CustomerDashboardCounter';
import { OrderStatusLabels } from '../../../models/enums/OrderStatus';
import { EventTypes } from '../../../models/enums/EventTypes';
import { TableSearchEventData } from '../../../models/core/table/TableSearchEventData';
import { NgIf, AsyncPipe } from '@angular/common';
import { TableActionButton } from '../../../models/core/table/TableActionButton';

@Component({
    selector: 'app-recent-orders',
    imports: [RouterLink, TableComponent, NgIf, AsyncPipe],
    templateUrl: './recent-orders.component.html',
    styleUrl: './recent-orders.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RecentOrdersComponent implements OnChanges {
    @Input() dashboardCounter = new CustomerDashboardCounter();

    //#region Table Variables
    public columns!: TableColumn[];
    public actions!: TableActionButton[];
    public totalRecords = 0;
    public pageIndex = 0;
    public pageSize = 10;
    public pageSizeOptions: number[] = [10, 50, 100];
    public searchOptions!: BaseSearchOptions;
    //#endregion

    private dataSubject = new BehaviorSubject<RecentOrderItem[]>([]);
    public data$ = this.dataSubject.asObservable();

    isToggled = false;

    constructor(
        public themeService: CustomizerSettingsService,
        private cdr: ChangeDetectorRef,
    ) {
        this.initialize();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['dashboardCounter'] && this.dashboardCounter) {
            const orders = this.dashboardCounter.recentOrders ?? [];
            this.totalRecords = orders.length;
            this.pageSize = orders.length || 10;
            this.dataSubject.next(orders);
            this.cdr.markForCheck();
        }
    }

    private initialize(): void {
        this.themeService.isToggled$.subscribe((isToggled) => {
            this.isToggled = isToggled;
        });
        this.searchOptions = new BaseSearchOptions(
            undefined,
            'createdOn',
            1,
            this.pageIndex,
            this.pageSize,
        );
        this.buildTable();
    }

    public buildTable(): void {
        this.columns = [
            new TableColumn(
                'orderNo',
                'Order No',
                FilterDataType.String,
                true,
                false,
                (row: RecentOrderItem) => `#${row.orderNo}`,
                true,
                { tooltip: 'Order No' },
            ),
            new TableColumn(
                'tenantName',
                'Tenant',
                FilterDataType.String,
                true,
                false,
                (row: RecentOrderItem) => row.tenantName ?? '',
                true,
                { tooltip: 'Tenant' },
            ),
            new TableColumn(
                'totalQty',
                'Qty',
                FilterDataType.Int,
                true,
                false,
                (row: RecentOrderItem) =>
                    row.totalQty != null ? `${row.totalQty}` : '-',
                true,
                { tooltip: 'Total Qty' },
            ),
            new TableColumn(
                'totalWeight',
                'Weight (kg)',
                FilterDataType.Decimal,
                true,
                false,
                (row: RecentOrderItem) =>
                    row.totalWeight != null
                        ? Number(row.totalWeight).toFixed(2)
                        : '-',
                true,
                { tooltip: 'Total Weight' },
            ),
            new TableColumn(
                'orderStatus',
                'Status',
                FilterDataType.String,
                true,
                false,
                (row: RecentOrderItem) =>
                    OrderStatusLabels[
                        row.orderStatus as keyof typeof OrderStatusLabels
                    ] ?? row.orderStatusLabel,
                true,
                { tooltip: 'Status' },
            ),
            new TableColumn(
                'createdOn',
                'Order Date',
                FilterDataType.DateTime,
                true,
                false,
                (row: RecentOrderItem) =>
                    row.createdOn
                        ? new Date(row.createdOn).toLocaleDateString('en-GB', {
                              day: '2-digit',
                              month: 'short',
                              year: 'numeric',
                          })
                        : '',
                true,
                { tooltip: 'Order Date' },
            ),
        ];

        this.actions = [];
    }

    public tableEvent(event: TableEvent<any>): void {
        if (event.type === EventTypes.TABLE) {
            const eventValue = event.data as TableSearchEventData;
            this.searchOptions.updateByObject(eventValue.baseSearchOptions);
            this.cdr.markForCheck();
        }
    }
}
